import { useQuery } from '@apollo/client';
import { graphql } from '@gql/gql';
import type { MtLitterGetAnimalsQuery, MtLitterGetAnimalsQueryVariables } from '@gql/graphql';
import { GenderEnum } from '@gql/graphql';
import type { SelectOption } from '@presentation/components';
import type { MtLitterPageFormOptions } from '@presentation/pages';
import { useCallback, useMemo } from 'react';
import { FaMars, FaVenus } from 'react-icons/fa';

export const MT_LITTER_GET_ANIMALS = graphql(`
  query MtLitterGetAnimals($animalsWhere: AnimalWhereInput) {
    accountMe {
      id

      animals(where: $animalsWhere) {
        id
        name
        gender
        dogInfos {
          pedigree
        }
      }
    }
  }
`);

export const useGetAnimals = () => {
  const variables = useMemo((): MtLitterGetAnimalsQueryVariables => {
    return {
      animalsWhere: {
        archived: { equals: false },
      },
    };
  }, []);

  const { data, error, loading } = useQuery<
    MtLitterGetAnimalsQuery,
    MtLitterGetAnimalsQueryVariables
  >(MT_LITTER_GET_ANIMALS, {
    variables,
    fetchPolicy: 'network-only',
  });

  const formatAnimalsSelectOptions = useCallback(
    (gender: GenderEnum) => {
      return (data?.accountMe?.animals ?? [])
        .filter((x) => x.gender === gender)
        .map((animal) => {
          let iconLeft;
          if (animal.gender === GenderEnum.F) iconLeft = <FaVenus />;
          if (animal.gender === GenderEnum.M) iconLeft = <FaMars />;
          return {
            value: animal.id,
            iconLeft,
            label: animal.name,
          } as SelectOption<number>;
        });
    },
    [data?.accountMe?.animals],
  );

  const animalOptionsByParentType = useMemo((): MtLitterPageFormOptions['animals'] => {
    return {
      FATHER: formatAnimalsSelectOptions(GenderEnum.M),
      MOTHER: formatAnimalsSelectOptions(GenderEnum.F),
    };
  }, [formatAnimalsSelectOptions]);

  return {
    animals: data?.accountMe?.animals,
    animalsLoading: loading,
    animalsError: error,
    animalOptionsByParentType,
  };
};
